import { Link } from "react-router-dom";
import {
  Truck,
  ShieldCheck,
  Package,
  Sparkles,
  Calendar,
  Layers,
  Boxes,
  GlassWater,
  Leaf,
  Recycle,
  RotateCcw,
  ArrowRight,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Service de livraison à domicile ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="overline" style={{ color: "#237cb5" }}>
              Logistique de confiance
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Service de livraison de cartons {city.aCity}
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Parce que la réussite de votre déménagement repose sur des
              garanties concrètes, notre service de livraison à domicile
              s&apos;engage chaque jour auprès des habitants {city.aCity}
              {" "}avec un professionnalisme rigoureux.
            </p>
            <p className="mt-4 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Nous prenons en charge l&apos;acheminement direct de tout
              votre matériel à plat, impeccablement propre et à
              l&apos;abri de l&apos;humidité, que vous habitiez dans le
              centre du village ou dans les secteurs environnants des
              Yvelines comme Crespières, Feucherolles, Thiverval-Grignon
              ou Chavenay. En planifiant le dépôt selon vos
              disponibilités exactes, nous éliminons les pertes de temps
              en magasin et la fatigue liée au transport de paquets
              encombrants dans votre voiture, vous permettant ainsi de
              commencer à emballer dès la réception en toute sérénité.
            </p>
          </div>

          <div className="lg:col-span-5 space-y-4">
            {[
              {
                title: "Dépôt direct à votre porte",
                body: "Davron, Crespières, Feucherolles, Thiverval-Grignon, Chavenay et villages environnants des Yvelines.",
                Icon: Truck,
              },
              {
                title: "Créneau au plus proche de vos dispos",
                body: "Vous fixez le moment du dépôt : nous nous adaptons à votre planning pour démarrer immédiatement l'emballage.",
                Icon: Calendar,
              },
              {
                title: "Matériel à plat, propre, à l'abri",
                body: "Cartons sous film de protection étanche, prêts à être dépliés et montés sans la moindre humidité.",
                Icon: ShieldCheck,
              },
            ].map(({ title, body, Icon }) => (
              <div
                key={title}
                className="flex items-start gap-4 bg-white border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
              >
                <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <div>
                  <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                    {title}
                  </h3>
                  <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                    {body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Vaisselle sans casse ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Vaisselle &amp; pièces fragiles
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Des solutions spécifiques pour transporter votre vaisselle
            sans casse
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Les assiettes, verres et plats délicats sont souvent les
            objets les plus stressants à emballer. Nous mettons à votre
            disposition des contenants techniques spécialement étudiés
            pour ces pièces fragiles.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
          {[
            {
              title: "Cartons à croisillons adaptés",
              body: "Nos cartons d'expert intègrent des séparateurs rigides qui maintiennent chaque verre et chaque assiette parfaitement immobile, évitant ainsi les frottements directs et les risques de fissure.",
              Icon: GlassWater,
            },
            {
              title: "Protection individuelle renforcée",
              body: "En associant ces structures à nos rouleaux de film à bulles haute densité, vous créez un véritable bouclier amortisseur capable de neutraliser les vibrations du camion de déménagement.",
              Icon: Layers,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="flex items-start gap-4 bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
            >
              <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Icon className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <div>
                <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
                <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            </div>
          ))}
        </div>

        <p className="mt-8 max-w-3xl text-[15px] md:text-base text-[#3a4856] leading-relaxed">
          Cette approche méthodique transforme l&apos;emballage de la
          cuisine en une tâche simple et rapide. En garantissant une
          isolation parfaite à chaque élément de votre vaisselle, vous
          gagnez un temps précieux au déballage et vous vous offrez la
          certitude de retrouver vos services de table intacts, prêts à
          être installés dans les placards de votre nouvelle cuisine
          {" "}{city.aCity}.
        </p>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/cartons-demenagement?categorie=croisillons"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-davron-cta-croisillons"
          >
            <GlassWater className="h-4 w-4" strokeWidth={2.6} />
            Voir les cartons à croisillons
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/emballage-protection"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-davron-cta-protection"
          >
            <Layers className="h-4 w-4" strokeWidth={2.6} />
            Film à bulles haute densité
          </Link>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Réduire son impact environnemental ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="overline" style={{ color: "#237cb5" }}>
              Éco-responsabilité
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Réduire son impact environnemental lors d&apos;un
              déménagement
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Parce qu&apos;il est aujourd&apos;hui indispensable de
              concilier efficacité logistique et respect de la nature,
              nous intégrons l&apos;écoresponsabilité au cœur de nos
              fournitures.
            </p>
            <p className="mt-4 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              En faisant le choix de ces solutions durables, vous limitez
              considérablement la production de déchets liée à votre
              changement d&apos;adresse. Une fois installé {city.aCity},
              vous participez activement à une économie circulaire
              responsable tout en ayant bénéficié d&apos;un niveau de
              sécurité maximal pour l&apos;intégralité de votre
              patrimoine.
            </p>

            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                to="/cartons-reconditionnes"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
                data-testid="city-davron-cta-recycle"
              >
                <Recycle className="h-4 w-4" strokeWidth={2.6} />
                Découvrir les cartons reconditionnés
                <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
              </Link>
              <Link
                to="/cartons-demenagement"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
                data-testid="city-davron-cta-shop"
              >
                <Boxes className="h-4 w-4" strokeWidth={2.6} />
                Voir toute la gamme
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5 space-y-4">
            {[
              {
                title: "Matériaux 100% recyclables",
                body: "Tous nos cartons sont fabriqués à partir de fibres de cellulose recyclées et assemblés à l'aide de colles végétales, ce qui garantit une biodégradabilité totale.",
                Icon: Leaf,
              },
              {
                title: "Robustesse et réutilisation",
                body: "Grâce à l'épaisseur de notre carton ondulé de qualité professionnelle, ces contenants ne s'affaissent pas après un seul usage. Ils se replient facilement pour être stockés, transmis à des proches ou déposés dans les filières de tri locales.",
                Icon: RotateCcw,
              },
              {
                title: "Économie circulaire locale",
                body: "Un cycle vertueux : moins de déchets d'emballage à usage unique, plus de matériel partagé entre voisins, familles et associations des Yvelines.",
                Icon: Sparkles,
              },
            ].map(({ title, body, Icon }) => (
              <div
                key={title}
                className="flex items-start gap-4 bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
              >
                <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <div>
                  <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                    {title}
                  </h3>
                  <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                    {body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-t border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-davron-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
